//#region imports
import React, { useEffect, useState, useMemo, useCallback } from "react";

// styling
import { Colors, Spacing } from "_styles";
import "./job-card.css"
import { mainContainer } from "./job-styles";

// redux
import { useSelector, useDispatch, useStore } from "react-redux";
import { selectUserDb } from "_redux/slices/main-screen/user";
import { setPoints, setClicked, setClickedJob, resetFlightContext, setClickedFromLocTab } from "_redux/slices/utils/menu-context";
import { setSelectedTask, setSelectedTaskList, setSelectedMultiplePAXTask, setSelectedMultiplePAXTaskError, setViewTask, setSelectedTaskToView } from "_redux/slices/timeline/selected-task";
import { selectFlightById } from "_redux/slices/timeline/timeline-flight-list";

// utils
import { handleNotesText, getTimeString, getCardClickedColor, getReadableTimeString, priorityAssistance } from "_utils/functions";
import { EmployeeJobCardWidth, JobCardWidth, minute30MinLayoutWidth } from "../timeline-constants";
import { PRMCodes } from "_utils/constants";
import { getAssistanceFromPassengerList } from "_utils/functions";

// custom components
import CardMiddle from "./job-card/card-middle";
import BreakTaskCard from "./break-task-card";
import AdhocTaskCard from "./adhoc-task-card";
import JobCardContainer from "./job-card/job-card-container";
import CardBottom from "./job-card/card-bottom";
import HoverOverlay from "./hover-overlay";
import { LinkedFlightInformation } from "../utils/linked-flight-information";
import ConnectionBadge from "../utils/connection-badge";
import JobNoteIcon from "./job-card/job-note-icon";


// icons
import { BsPersonWheelchair } from "react-icons/bs";
import { RiPassPendingLine } from "react-icons/ri";
import { PiTruck } from "react-icons/pi";

//#endregion

//#region renders
const JobCard = (props) => {
    //#region redux
    const dispatch = useDispatch();
    const store = useStore();
    const userDb = useSelector(selectUserDb);
    //#endregion

    //#region props

    // selected task info
    const selectedTask = props?.selectedTask;
    const selectedTasksList = props?.selectedTasksList;
    const selectedMultiplePAXTask = props?.selectedMultiplePAXTask;

    // layout + styling
    const usedTheme = props?.usedTheme || {}; // Fallback to empty object if not provided
    const thirtyMinLayout = props?.thirtyMinLayout;

    //#endregion

    //#region local states
    const job = props.job || {};

    // OPTIMIZED: Memoize iata
    const iata = useMemo(() => userDb?.details?.authority || "", [userDb?.details?.authority]);

    // Construct flight key when flightData is not provided
    const flightKey = useMemo(() => {
        // If flightData is provided, no need to construct key
        if (props?.flightData) return null;

        // Try to construct from job data
        // Check if job has flightKey property
        if (job?.flightKey) return job.flightKey;

        // Construct from flightConfirmationDetails
        if (job?.flightConfirmationDetails?.carrierCode &&
            job?.flightConfirmationDetails?.flightNumber &&
            job?.flightConfirmationDetails?.origin &&
            job?.flightConfirmationDetails?.destination &&
            job?.flightConfirmationDetails?.departureDate) {
            return `${job.flightConfirmationDetails.carrierCode}${job.flightConfirmationDetails.flightNumber}_${job.flightConfirmationDetails.origin}_${job.flightConfirmationDetails.destination}_${job.flightConfirmationDetails.departureDate}`;
        }

        // Construct from flightInformation
        if (job?.flightInformation?.flightNumber &&
            job?.flightInformation?.departureAirport &&
            job?.flightInformation?.arrivalAirport) {

            // Use departureDate or date as fallback
            const flightDate = job?.flightInformation?.departureDate || job?.flightInformation?.date;

            if (flightDate) {
                return job?.combinedBooking
                    ? `${job.flightInformation.flightNumber}_${job.flightInformation.departureAirport}_${job.flightInformation.arrivalAirport}_${flightDate}_BOARDING`
                    : `${job.flightInformation.flightNumber}_${job.flightInformation.departureAirport}_${job.flightInformation.arrivalAirport}_${flightDate}`;
            }
        }

        return null;
    }, [props?.flightData, job?.flightKey, job?.flightConfirmationDetails, job?.flightInformation]);

    // Create a memoized selector that returns null when no flightKey
    const flightSelector = useMemo(() => {
        if (!flightKey) return () => null;
        return selectFlightById(flightKey);
    }, [flightKey]);

    // Fetch flight data from Redux when flightData prop is not provided
    const flightFromRedux = useSelector(flightSelector);

    // OPTIMIZED: Memoize flightData calculation
    const flightData = useMemo(() => {
        if (props?.toConfirm && Array.isFullArray(props?.flightsArray) && job?.jobType !== "ramp-task" && job?.jobType !== "boarding-task") {
            const matchingFlights = props?.flightsArray?.filter((flight) => flight?.flightNumber == job?.flightConfirmationDetails?.carrierCode + job?.flightConfirmationDetails?.flightNumber);
            // A turnaround has the same flight number inbound and outbound on the same day, so the
            // booking's route has to be matched too. When several legs match the number and none
            // match the route, resolve nothing rather than guessing the wrong direction.
            const foundFlight = matchingFlights?.find((flight) =>
                flight?.origin == job?.flightConfirmationDetails?.origin &&
                flight?.destination == job?.flightConfirmationDetails?.destination
            ) ?? (matchingFlights?.length === 1 ? matchingFlights[0] : null);
            return foundFlight || {
                ...props?.job?.flightConfirmationDetails,
                flightNumber: job?.flightConfirmationDetails?.carrierCode + job?.flightConfirmationDetails?.flightNumber
            } || job?.flightInformation || {};
        }
        // Use flightData prop if provided
        if (props?.flightData) return props.flightData;
        // Use flight data from Redux if available
        if (flightFromRedux) return flightFromRedux;
        // Fallback to job flightInformation
        return job?.flightInformation || {};
    }, [
        props?.toConfirm,
        props?.flightsArray,
        props?.flightData,
        flightFromRedux,
        job?.flightConfirmationDetails,
        job?.flightInformation,
        job?.jobType
    ]);
    // Identity of the flight document this card resolved (from the timeline row / redux).
    // The assignment confirmation reuses this exact document instead of re-deriving it
    // from a booking timestamp, which can land on the wrong day for post-midnight arrivals
    // (e.g. EI449 HER→DUB landing just after midnight, giving the neighbouring day's gate).
    const resolvedFlightDocId = useMemo(
        () => flightData?.flightId ?? flightData?._id ?? null,
        [flightData?.flightId, flightData?._id]
    );

    // OPTIMIZED: Memoize flight number
    const flightNumber = useMemo(() => {
        const fcdFlightNumber = job?.flightConfirmationDetails?.carrierCode && job?.flightConfirmationDetails?.flightNumber
            ? `${job?.flightConfirmationDetails?.carrierCode}${job?.flightConfirmationDetails?.flightNumber}`
            : "";
        return flightData.flightNumber ||

            job?.flightInformation?.flightNumber || fcdFlightNumber || "";
    }, [flightData.flightNumber, , job?.flightInformation?.flightNumber, job?.flightConfirmationDetails?.carrierCode, job?.flightConfirmationDetails?.flightNumber]);

    // OPTIMIZED: Memoize displayFlightNumber
    const displayFlightNumber = useMemo(() =>
        flightNumber.replace("U2", "EZY"),
        [flightNumber]
    );

    // OPTIMIZED: Memoize flightType
    const flightType = useMemo(() => {
        if (flightData?.type) return flightData.type;
        if (job?.flightConfirmationDetails?.origin == iata) return 'departure';
        if (job?.flightInformation?.departureAirport == iata) return 'departure';
        return 'arrival';
    }, [flightData?.type, job?.flightConfirmationDetails?.origin, job?.flightInformation?.departureAirport, iata]);

    // OPTIMIZED: Memoize departure
    const departure = useMemo(() => {
        return flightData.origin ||
            job?.flightConfirmationDetails?.origin ||
            job?.flightInformation?.departureAirport || "";
    }, [flightData.origin, job?.flightConfirmationDetails?.origin, job?.flightInformation?.departureAirport]);

    // OPTIMIZED: Memoize arrival
    const arrival = useMemo(() => {
        return flightData.destination ||
            job?.flightConfirmationDetails?.destination ||
            job?.flightInformation?.arrivalAirport || "";
    }, [flightData.destination, job?.flightConfirmationDetails?.destination, job?.flightInformation?.arrivalAirport]);

    // OPTIMIZED: Memoize time
    const time = useMemo(() => {
        if (flightData.operationTime) return getTimeString(flightData.operationTime);
        if (flightData?.flightTime) return getTimeString(flightData.flightTime);
        if (job?.flightConfirmationDetails?.departureTime) return getTimeString(new Date(job?.flightConfirmationDetails?.departureTime));
        if (job?.flightInformation?.scheduledTime) return getTimeString(new Date(job?.flightInformation?.scheduledTime));
        return "";
    }, [
        flightData.operationTime,
        flightData?.flightTime,
        job?.flightConfirmationDetails?.departureTime,
        job?.flightInformation?.scheduledTime
    ]);

    // OPTIMIZED: Memoize gate information
    const gateInformation = useMemo(() => {
        return flightData.gate && flightData.gate !== "N/A" ? flightData.gate : "";
    }, [flightData.gate]);

    // OPTIMIZED: Memoize stand information
    const standInformation = useMemo(() => {
        return flightData.stand && flightData.stand !== "N/A" ? flightData.stand : "";
    }, [flightData.stand]);

    const linkedFlightFromFlightData = flightData?.linkedFlight ?? null;
    const linkedFlightKey = flightData?.linkedFlightKey ?? linkedFlightFromFlightData?.FlightKey ?? job?.linkedFlightKey ?? job?.linkedFlight?.FlightKey ?? null;
    const linkedFlightIdentity = flightData?.linkedFlightIdentity ?? linkedFlightFromFlightData?.FlightIdentity ?? job?.linkedFlightIdentity ?? job?.linkedFlight?.FlightIdentity ?? "";
    const [showLinkedFlightOverlay, setShowLinkedFlightOverlay] = useState(false);
    const [overlayPosition, setOverlayPosition] = useState({ x: 0, y: 0 });

    const handleLinkedFlightMouseEnter = useCallback((e) => {
        setShowLinkedFlightOverlay(true);
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    const handleLinkedFlightMouseLeave = useCallback(() => {
        setShowLinkedFlightOverlay(false);
    }, []);

    const handleLinkedFlightMouseMove = useCallback((e) => {
        setOverlayPosition({ x: e.clientX, y: e.clientY });
    }, []);

    // task info 
    const [jobHasNote, setJobHasNote] = useState(false)                                                                // show note icon if booking has a note.

    // pax info
    const { displayedAssistance, fullAssistance } = useMemo(() => {


        // check if we have passenger list
        if (job?.passengers && job.passengers.length != 0) {
            let assistance = getAssistanceFromPassengerList(job?.passengers);

            // check if longer than 30 characters
            if (assistance.length > 9) {
                assistance = assistance.substring(0, 9) + '+';
            }

            return {
                displayedAssistance: assistance,
                fullAssistance: getAssistanceFromPassengerList(job?.passengers)
            }
        }

        else if (job.assistance?.includes("PLACEHOLDER") || job.passengerInformation?.assistance?.includes("PLACEHOLDER")) {
            return {
                displayedAssistance: "Required",
                fullAssistance: "Required update assistance by agent"
            }
        }

        // extract assistance codes from job assistance strings OR passengerInformation.assistance (for ALT pickups / gate relocations)
        const jobAssistance = job?.assistance || [];
        const passengerAssistance = job?.passengerInformation?.assistance || [];
        const combinedAssistance = [...jobAssistance, ...passengerAssistance];

        // check assistance codes in the combined assistance array
        const assistArray = combinedAssistance?.flatMap(a => a?.match(/\b[A-Z]{4}\b/g) || []) || [];
        // check for valid assistance codes
        const valid = priorityAssistance([...new Set(assistArray)]?.filter(c => PRMCodes?.includes(c)));
        // default assistance
        if (!valid.length) return { displayedAssistance: "WCHR", fullAssistance: "WCHR" };

        // return assistance display values
        return {
            displayedAssistance: valid.length > 1 ? `${valid[0]}.+${valid.length - 1}` : valid[0],
            fullAssistance: valid.join("")
        };

    }, [job?.assistance, job?.passengers, job?.passengerInformation?.assistance]);

    // modals / card states
    const [toggleAcceptModal, setToggleAcceptModal] = useState(false);                                              // shows task accept modal

    // check if job is clicked
    const isClicked = useMemo(() => {
        if (!selectedTasksList || !job._id) return false;
        return selectedTasksList.some(t => t._id === job._id);
    }, [selectedTasksList, job._id]);

    // OPTIMIZED: Memoize jobHasNote calculation to reduce useEffect dependency
    const hasNote = useMemo(() => {
        if (job?.flightBooking && job?.originalBookingDoc?.notes) return true;
        if (job?.notes || job?.taskNotes) return true;
        return false;
    }, [job?.flightBooking, job?.originalBookingDoc?.notes, job?.notes, job?.taskNotes]);
    //#endregion

    //#region useEffects

    // OPTIMIZED: Combined the two useEffects into one
    useEffect(() => {
        setJobHasNote(hasNote);
    }, [hasNote]);


    //#endregion

    //#region functions

    // OPTIMIZED: Memoize handlers with useCallback
    const handleLeftClick = useCallback((e) => {
        // check if the job is break task and completed don't open context menu
        if (job?.completed && job?.isBreakTask) return;
        if (job?._id?.startsWith("CONNECT-") || job?.isConnectBooking) return;
        e.preventDefault();

        // open context menu
        dispatch(setClicked(true));
        dispatch(setPoints({ x: e.pageX, y: e.pageY }));
        dispatch(setClickedJob(job));
        dispatch(resetFlightContext());
        if (props?.fromLocationPanel) {
            dispatch(setClickedFromLocTab(true));
        } else {
            dispatch(setClickedFromLocTab(false));
        }
    }, [job?.completed, job?.isBreakTask, job, dispatch]);

    // OPTIMIZED: Memoize handleJobClick
    const handleJobClick = useCallback((e) => {
        e.stopPropagation();
        console.log("job:", job);
        if ((job?._id?.startsWith("CONNECT-") || job?.isConnectBooking)) {
            return;
        }

        // For allocated tasks - open task information modal
        if (job && (job?.employeeId || job?.assignedTime || props?.disabled)) {
            dispatch(setSelectedTaskToView(job));
            dispatch(setViewTask(true));
            return;
        }

        // check if the pax is rejected 
        if (job?.rejectedPax == true || job?.originalBookingDoc?.rejectedPax == true) return;

        // check if there is a list of job selected and viewed to be assigned
        if (props?.toConfirm == true) {
            if (props?.forMultiplePaxAssign == true) {
                let tempSelectedMultiplePAXTask = Array.isFullArray(selectedMultiplePAXTask) ? [...selectedMultiplePAXTask] : [];

                if (job?.combinedBooking) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for combined bookings to assign to the same employee at the same time"));
                    return;
                } else if (job?.combinedRampBooking) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for combined ramp bookings to assign to the same employee at the same time"));
                    return;
                } else if (job?.isALTPickUp) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for ALT pickup tasks to assign to the same employee at the same time"));
                    return;
                } else if (job?.isBreakTask) {
                    dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for break tasks to assign to the same employee at the same time"));
                    return;
                }

                if (tempSelectedMultiplePAXTask?.map((item) => item?._id)?.includes(job?._id)) {
                    tempSelectedMultiplePAXTask = tempSelectedMultiplePAXTask?.filter((item) => item?._id != job?._id);
                }
                else {
                    const firstSelectedTask = Array.isFullArray(tempSelectedMultiplePAXTask) ? tempSelectedMultiplePAXTask?.[0] : null;

                    const selectedFlightNumber = firstSelectedTask?.flightConfirmationDetails?.carrierCode + firstSelectedTask?.flightConfirmationDetails?.flightNumber;
                    if (selectedFlightNumber && selectedFlightNumber !== job?.flightConfirmationDetails?.carrierCode + job?.flightConfirmationDetails?.flightNumber) {
                        dispatch(setSelectedMultiplePAXTaskError("Cannot select multiple pax for different flights to assign to the same employee at the same time"));
                        return;
                    } else {
                        tempSelectedMultiplePAXTask.push({ ...job, resolvedFlightDocId });
                    }
                }
                dispatch(setSelectedMultiplePAXTask(tempSelectedMultiplePAXTask));
                dispatch(setSelectedMultiplePAXTaskError(null));
            }
            else {
                // check if selected task is null and if its the same task
                if (selectedTask != null && selectedTask?._id == job?._id) {
                    // selected task is current job, unselect it
                    if (props?.clicked?.includes(job?.["_id"])) {
                        props?.setClicked(null);
                        dispatch(setSelectedTask(null));
                    }
                } else {
                    props?.setClicked(job?.["_id"]);
                    dispatch(setSelectedTask({ ...job, resolvedFlightDocId }));
                }
            }
        }

        else {

            // Read the latest list directly from the store so the value is
            // never stale, even when React.memo skips re-rendering this card.
            const currentList = store.getState().selectedTask.selectedTasksList;
            let selectedTasksIds = Array.isFullArray(currentList)
                ? currentList?.map((element) => element?._id)
                : [];
            // check if the selected tasks list include the selected task or not
            if (selectedTasksIds?.includes(job["_id"])) {
                // remove the clicked task from selected tasks list
                dispatch(
                    setSelectedTaskList(
                        currentList?.filter((item) => item?._id != job["_id"])
                    )
                );
            }
            else {
                dispatch(setSelectedTaskList([...currentList, { ...job, resolvedFlightDocId }]));
            }
        }
    }, [job, selectedTask, selectedMultiplePAXTask, props, dispatch, store, resolvedFlightDocId]);

    //#endregion

    //#region card styling
    // OPTIMIZED: Memoize cardStyle object
    const cardStyle = useMemo(() => ({
        ...mainContainer,
        //borderColor: handleCardTopColor(job, flightType),
        //borderTopColor: handleCardTopColor(job, flightType),
        backgroundColor: props?.jobConfirmation ? usedTheme?.BACKGROUND_COLOR : isClicked ? getCardClickedColor(job, flightType) : usedTheme?.BACKGROUND_COLOR,
        width: '100%',
        position: props.position ? props.position : 'relative',
        borderWidth: isClicked ? '2px' : job?.paxManualCheckIn ? '2px' : job?.originalBookingDoc?.paxManualCheckIn ? '2px' : '1px',
        borderColor: isClicked ? Colors.PRM_LIGHT_BLUE : job?.paxManualCheckIn ? '#6F42C1' : job?.originalBookingDoc?.paxManualCheckIn ? '#6F42C1' : 'lightgray',
        color: usedTheme?.TEXT_COLOR,
        maxHeight: "73px",
        cursor: 'pointer',
    }), [job, flightType, props?.jobConfirmation, props?.disabled, props.position, isClicked, usedTheme, thirtyMinLayout]);

    //#endregion

    //#region render error / break card

    // error checking
    if (job == undefined || job["_id"] == undefined) {
        return <p></p>;
    }

    // break task - Safari compatibility: use explicit boolean check
    if (job.isBreakTask === true) {

        // Calculate margin offset based on placementOffset (minutes from 30-min interval)
        // Only apply in 30-min layout mode
        const offsetMargin = thirtyMinLayout && job?.placementOffset ? job.placementOffset * minute30MinLayoutWidth : 0;

        return <div style={{
            display: "flex",
            justifyContent: "center",
            position: props.position ? props.position : 'relative',
            cursor: 'pointer',
            alignItems: 'center',
            background: 'transparent',
            marginLeft: offsetMargin,
        }}>
            <BreakTaskCard
                job={job}
                disabled={props.disabled}
                thirtyMinLayout={thirtyMinLayout}
                showFullCard={props?.showFullCard}
                handleLeftClick={handleLeftClick}
                handleJobClick={handleJobClick}
                setToggleAcceptModal={setToggleAcceptModal}
                toggleAcceptModal={toggleAcceptModal}
                closeModal={() => setToggleAcceptModal(false)}
                cardStyle={cardStyle}
            />
        </div>

    }

    if (job.isAdhocTask === true) {
        return <div style={{
            display: "flex",
            justifyContent: "center",
            position: props.position ? props.position : 'relative',
            cursor: 'pointer',
            alignItems: 'center',
            background: 'transparent',
        }}>
            <AdhocTaskCard
                job={job}
                disabled={props.disabled}
                thirtyMinLayout={thirtyMinLayout}
                showFullCard={props?.showFullCard}
                handleLeftClick={handleLeftClick}
                handleJobClick={handleJobClick}
                setToggleAcceptModal={setToggleAcceptModal}
                toggleAcceptModal={toggleAcceptModal}
                closeModal={() => setToggleAcceptModal(false)}
                cardStyle={cardStyle}
            />
        </div>
    }
    //#endregion

    return (
        <div>

            {/* job card */}
            <div
                onDoubleClick={handleLeftClick}
                onClick={handleJobClick}
                style={{
                    display: "flex",
                    justifyContent: "center",
                    position: props.position ? props.position : 'relative',
                    cursor: 'pointer',
                    alignItems: 'center',
                }}>

                <JobCardContainer job={job} thirtyMinLayout={thirtyMinLayout} disabled={props.disabled} showFullCard={props?.showFullCard}>

                    <div onContextMenu={handleLeftClick} style={cardStyle}>

                        {/* top half of card (flight number, destination / arrival airport / gate + stand info / job color */}
                        <CardBottom
                            job={job}
                            flightNumber={flightNumber}
                            displayFlightNumber={displayFlightNumber}
                            iata={flightType == 'departure' || flightType == 'boarding' ? arrival : departure}
                            flightType={flightType}
                            userIata={userDb?.details?.authority}
                            fromLocationPanel={props?.fromLocationPanel}
                        />

                        {/* bottom half of card (assistance, note icon + flight time */}
                        <div key={fullAssistance || "default"} style={{ display: "flex", width: '100%', justifyContent: "space-between", alignItems: 'center' }}>

                            {/* assistance */}
                            <div className="tippybox" style={{ marginLeft: Spacing.SCALE_2 }} {...(fullAssistance != '' ? { 'data-tippy-content': fullAssistance } : {})} >
                                <p style={{ margin: "0px 0", textAlign: "center", fontSize: 10, fontWeight: 'bold' }}>{displayedAssistance}</p>
                            </div>

                            {/* HC */}
                            {
                                props.job?.assignedAsHC && (
                                    <div className="tippybox" style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginLeft: Spacing.SCALE_2 }} {...(props.job.assignedAsHC ? { 'data-tippy-content': "Assigned as HC" } : {})} >
                                        <BsPersonWheelchair size={14} />
                                    </div>
                                )
                            }
                            {/* Remote checkin tasks */}
                            {
                                (props.job?.paxManualCheckIn || props.job?.originalBookingDoc?.paxManualCheckIn) && (
                                    <div style={{background: '#6F42C1', padding: '0.5px 2px', borderRadius: '5px', fontSize: '9px', color: '#fff', margin: '1px'}} data-tippy-content="Remote Check-In">
                                        RC
                                    </div>
                                )
                            }

                            {/* pax at a location that is displayed under another location's card */}
                            {props.job?.mergedFromLocation && (
                                <div style={{background: '#0d6efd', padding: '0.5px 2px', borderRadius: '5px', fontSize: '9px', color: '#fff', margin: '1px', whiteSpace: 'nowrap', flexShrink: 0, lineHeight: 1.2}} data-tippy-content={`At ${props.job.mergedFromLocation}`}>
                                    {props.job.mergedFromLocation.trim().split(/\s+/).pop().toUpperCase()}
                                </div>
                            )}

                            {/* Needs update data */}
                            {(props?.job?.notComplete || props?.job?.originalBookingDoc?.notComplete) &&
                                <div className="tippybox" style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginLeft: Spacing.SCALE_2 }} {...(props.job.notComplete ? { 'data-tippy-content': "Name or assistance is missing." } : {})} >
                                    <RiPassPendingLine size={14} />
                                </div>}
                            {props.job?.ambiliftRequired &&
                                <div className="tippybox" style={{ flex: 1, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', marginLeft: Spacing.SCALE_2 }} {...(props.job.ambiliftRequired ? { 'data-tippy-content': "Ambilift Required" } : {})} >
                                    <PiTruck size={14} />
                                </div>}

                            {/* Connection passenger: onwards (arrival) or inbound (departure) */}
                            {(job?.originalBookingDoc?.onwards || job?.inbound || job?.connection) && (
                                <ConnectionBadge
                                    onwards={job?.originalBookingDoc?.onwards}
                                    inbound={job?.inbound}
                                    variant="icon"
                                    iconSize={18}
                                />
                            )}

                            {/* note icon */}
                            <JobNoteIcon job={job} hasNote={jobHasNote} />

                            {/* flight time */}
                            <div style={{ position: 'relative', minHeight: '20px', minWidth: '40px' }}>
                                {iata === 'DUB' && (
                                    <HoverOverlay
                                        isVisible={showLinkedFlightOverlay}
                                        onMouseEnter={handleLinkedFlightMouseEnter}
                                        onMouseLeave={handleLinkedFlightMouseLeave}
                                        onMouseMove={handleLinkedFlightMouseMove}
                                        position={overlayPosition}
                                        title="Linked Flight"
                                    >
                                        <LinkedFlightInformation
                                            linkedFlightIdentity={linkedFlightIdentity}
                                            linkedFlightKey={linkedFlightKey}
                                        />
                                    </HoverOverlay>
                                )}
                                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                                    <p style={{ margin: "0px 0", textAlign: "center", fontSize: 10, marginRight: Spacing.SCALE_2 }}>
                                        {time}
                                    </p>
                                </div>
                            </div>

                        </div>

                        {/* horizontal line break */}
                        <hr style={{ margin: "0px" }} />

                        {/* middle half of card (pax name + location name + status) */}
                        <CardMiddle
                            job={job}
                            flightType={flightType}
                            gateInformation={gateInformation}
                            standInformation={standInformation}
                        />


                    </div>

                </JobCardContainer>

            </div>

        </div>
    );
};
//#endregion

const isJobInList = (list, jobId) => {
    if (!list || !jobId) return false;
    return list.some(t => t?._id === jobId);
};

export default React.memo(JobCard, (prev, next) => {
    const jobId = prev.job?._id;
    const prevIsSelected = isJobInList(prev.selectedTasksList, jobId);
    const nextIsSelected = isJobInList(next.selectedTasksList, jobId);

    return (
        prev.job === next.job &&
        prev.job?.assistance === next.job?.assistance &&
        prev.job?.passengers === next.job?.passengers &&
        prev.disabled === next.disabled &&
        prev?.forMultiplePaxAssign === next?.forMultiplePaxAssign &&
        prev.index === next.index &&
        prev.thirtyMinLayout === next.thirtyMinLayout &&
        prev.selectedTask?._id === next.selectedTask?._id &&
        prevIsSelected === nextIsSelected &&
        prev.usedTheme === next.usedTheme &&
        prev.selectedMultiplePAXTask === next.selectedMultiplePAXTask &&
        prev.flightData === next.flightData &&
        prev.flightData?.gate === next.flightData?.gate &&
        prev.flightData?.stand === next.flightData?.stand
    );
});